import type { Location } from "./types";

/**
 * CORE LOCATIONS — the ten pages already live, plus the town hubs that
 * Operating Brief §5.4 identifies as the highest-earning tier.
 *
 * Every airspace record carries a `source` and a `lastVerified` date. Entries
 * marked `regional-inference` are derived from the corridor's known control-zone
 * geometry and are NOT site-confirmed; their copy narrows the claim to the
 * corridor and states that the specific site is checked against NAV Drone before
 * the shoot. That is ladder level 2 (Operating Brief §6.4) — honest, useful, and
 * the correct thing to publish until a per-site confirmation exists.
 *
 * No entry claims routine flight where `flightStatus` is restricted.
 */
export const CORE_LOCATIONS: Location[] = [
  // ─────────────────────────────────────────────────────────────── CALGARY ──
  {
    slug: "calgary",
    name: "Calgary",
    region: "calgary-centre",
    city: "Calgary",
    province: "Alberta",
    coordinates: { lat: 51.0447, lng: -114.0719 },
    tier: 0,
    driveMinutesFromCalgary: 0,
    airspace: {
      classification: "Class C control zone (CYYC)",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 13,
      authorizationRequired: true,
      authorizationNote:
        "Most of the city sits inside the Calgary International control zone. Flights are conducted under an Advanced RPAS certificate with NAV CANADA authorisation filed before the shoot is confirmed.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
      restrictedZones: [
        "Calgary International Airport (CYYC) approach paths",
        "Foothills Medical Centre heliport",
        "Peter Lougheed Centre heliport",
      ],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Calgary is one of the few Canadian cities where the downtown core, the airport approach and a busy training aerodrome all overlap the same metropolitan area.",
      consequence:
        "An operator without an Advanced certificate cannot legally fly most Calgary addresses, which is why so many quotes quietly exclude the inner city and the northeast.",
      resolution:
        "Advanced RPAS certification means the authorisation is filed as part of booking rather than treated as an obstacle — the shoot date is confirmed once it is in hand.",
    },
    landmarks: [
      "Stephen Avenue",
      "Memorial Drive",
      "the Bow River pathway",
      "Crowchild Trail",
      "Deerfoot Trail",
    ],
    terrain:
      "A prairie city cut by two river valleys. The Bow and the Elbow carve escarpments through the middle of it, so elevation change is concentrated along the water rather than spread across the grid.",
    windNote:
      "Chinook country. A winter chinook can lift surface wind from calm to gusting inside an hour, and the gust spread matters more to an airframe than the average speed.",
    seasonalWindow:
      "At 51° north the golden hour swings from roughly 4pm in late December to past 9pm in June. Late-summer wildfire smoke is the main threat to contrast between July and early September.",
    bylawNote:
      "City of Calgary parks bylaws restrict launching and landing in municipal parks. Takeoff and landing are planned from private property with the owner's permission.",
    subjectMix: [
      "luxury-residential",
      "commercial-retail",
      "construction-progress",
      "new-build-subdivision",
      "municipal-infrastructure",
    ],
    mobilisationNote:
      "Home base. Same-day scheduling is realistic when weather and authorisation align.",
    faqs: [
      {
        question: "Can you legally fly a drone in Calgary?",
        answer:
          "Yes. Most of Calgary sits inside the Calgary International (CYYC) Class C control zone, which means flights require an Advanced RPAS certificate and a NAV CANADA authorisation. Fly4MEdia holds Advanced certification and files that authorisation before confirming your shoot date.",
      },
      {
        question: "Why do some Calgary drone quotes exclude the northeast?",
        answer:
          "Northeast Calgary sits closest to the CYYC approach paths, so it carries the tightest authorisation requirements in the city. Operators holding only a Basic RPAS certificate cannot fly there at all, so they exclude it rather than explain it.",
      },
      {
        question: "How far in advance should a Calgary shoot be booked?",
        answer:
          "Book far enough ahead to absorb one weather cancellation. Airspace authorisation is filed as part of booking, so the variable that moves a Calgary shoot date is almost always wind or cloud ceiling, not paperwork.",
      },
    ],
    primaryKeywords: [
      "drone photography Calgary",
      "aerial photography Calgary",
      "real estate drone video Calgary",
    ],
    nearestLocations: ["aspen-woods", "chestermere", "airdrie", "springbank", "bearspaw"],
  },

  // ────────────────────────────────────────────────────────────── COCHRANE ──
  {
    slug: "cochrane",
    name: "Cochrane",
    region: "cochrane-foothills",
    city: "Cochrane",
    province: "Alberta",
    coordinates: { lat: 51.1892, lng: -114.4686 },
    tier: 1,
    driveMinutesFromCalgary: 35,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Springbank Airport (CYBW)",
      distanceToAerodromeKm: 22,
      authorizationRequired: false,
      authorizationNote:
        "Cochrane sits west of the Springbank control zone in uncontrolled airspace. The eastern approach to town runs nearer the CYBW boundary than the town limits suggest, so the specific address is confirmed against NAV Drone before every flight.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Cochrane is built on a hillside above the Bow River, so the town has roughly 100 metres of vertical relief between the valley floor and the upper benches.",
      consequence:
        "A ground-level listing photo flattens that entirely — the buyer cannot see that the house sits above the valley, which is the single thing they are paying for.",
      resolution:
        "A rising reveal from valley floor to bench height puts the elevation back into the frame, delivered in the standard turnaround.",
    },
    landmarks: [
      "GlenEagles",
      "Sunset Ridge",
      "Riversong",
      "Heritage Hills",
      "the Bow River valley",
    ],
    terrain:
      "A hillside town on the north bank of the Bow, with benches stepping up from the river and the foothills rising immediately west.",
    windNote:
      "Squarely in the chinook belt. West wind accelerates as it comes off the foothills, and the valley below town channels it further.",
    seasonalWindow:
      "West-facing, so the last light of the day is the town's best light. Snow cover typically holds on the upper benches after it has gone from Calgary.",
    bylawNote:
      "Town of Cochrane park and pathway rules govern launch and landing on municipal land; private-property launch is the default.",
    subjectMix: ["luxury-residential", "acreage-estate", "new-build-subdivision", "commercial-retail"],
    mobilisationNote:
      "About 35 minutes from the Calgary base on Highway 1A or Highway 1. Comfortably a same-day booking.",
    faqs: [
      {
        question: "Do you need airspace authorisation to fly a drone in Cochrane?",
        answer:
          "Generally no. Cochrane sits west of the Springbank (CYBW) control zone in uncontrolled Class G airspace. The eastern edge of town runs closer to that boundary than the town limits imply, so the specific address is confirmed against NAV Drone before every flight.",
      },
      {
        question: "What makes Cochrane properties different to shoot?",
        answer:
          "Elevation. The town steps up from the Bow River in benches, so a home's position above the valley is often its main value driver. That relationship is invisible in ground photography and obvious from 90 metres.",
      },
      {
        question: "When is the best time of day for a Cochrane shoot?",
        answer:
          "Late afternoon into the last hour of daylight. Cochrane faces west over the Bow valley toward the foothills, so evening light rakes across the benches and separates the terraces instead of flattening them.",
      },
    ],
    primaryKeywords: [
      "drone photography Cochrane",
      "Cochrane real estate drone video",
      "aerial photography Cochrane Alberta",
    ],
    nearestLocations: ["gleneagles", "bearspaw", "ghost-lake", "springbank", "bragg-creek"],
  },

  // ─────────────────────────────────────────────────────────────── CANMORE ──
  {
    slug: "canmore",
    name: "Canmore",
    region: "bow-valley",
    city: "Canmore",
    province: "Alberta",
    coordinates: { lat: 51.0884, lng: -115.3479 },
    tier: 1,
    driveMinutesFromCalgary: 75,
    airspace: {
      classification: "Class G uncontrolled, adjacent to Banff National Park",
      nearestAerodrome: "Canmore municipal heliport",
      distanceToAerodromeKm: 2,
      authorizationRequired: false,
      authorizationNote:
        "Canmore itself is uncontrolled airspace, but the Banff National Park boundary sits a short distance west and the municipal heliport is inside the town. Every Bow Valley flight is planned against the park line and the heliport circuit first.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
      restrictedZones: [
        "Banff National Park boundary (west)",
        "Canmore municipal heliport circuit",
      ],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "The Banff National Park boundary sits only a few kilometres west of Canmore, and Parks Canada prohibits drone operation inside it without a permit.",
      consequence:
        "A shot list written from a map rather than from the boundary can put half its frames inside a no-fly area, and the client finds out on the day.",
      resolution:
        "The park line is the first thing plotted on a Bow Valley flight plan, so what is delivered is what was promised.",
    },
    landmarks: [
      "Three Sisters",
      "Silvertip",
      "Spring Creek",
      "Cougar Creek",
      "the Bow Valley Trail",
    ],
    terrain:
      "A steep-sided mountain valley. Peaks rise well over a kilometre above the valley floor on both sides, which compresses the usable light window at both ends of the day.",
    windNote:
      "The valley funnels wind along its axis and accelerates it. Mid-valley conditions can differ sharply from the readings at either end.",
    seasonalWindow:
      "The mountains take the sun early. Effective golden hour is shorter than the almanac suggests, particularly in the shoulder seasons, and the valley holds snow well into spring.",
    bylawNote:
      "Town of Canmore rules govern municipal land, and the Banff National Park prohibition applies immediately west of the town boundary.",
    subjectMix: ["resort-terrain", "tourism-hospitality", "luxury-residential", "acreage-estate"],
    mobilisationNote:
      "About 75 minutes from Calgary on the Trans-Canada. Practical as a day trip; the drive is usually shorter than the wait for the right light.",
    faqs: [
      {
        question: "Can you fly a drone in Canmore?",
        answer:
          "Yes. Canmore sits in uncontrolled Class G airspace outside Banff National Park. The park boundary is only a few kilometres west and the town has a municipal heliport, so both are plotted on the flight plan before the shoot.",
      },
      {
        question: "Can you fly from Canmore into Banff National Park?",
        answer:
          "No. Parks Canada prohibits launching, landing and operating a drone inside the park without a permit, and proximity does not change that. Frames that require park airspace are identified and removed from the shot list before booking.",
      },
      {
        question: "Why is the light window shorter in Canmore than in Calgary?",
        answer:
          "The valley walls. Peaks rising more than a kilometre on both sides put the valley floor into shadow well before the sun actually sets, so a Canmore shoot is scheduled against terrain, not against the published sunset time.",
      },
    ],
    primaryKeywords: [
      "drone photography Canmore",
      "Canmore aerial video",
      "mountain real estate drone Canmore",
    ],
    nearestLocations: ["harvie-heights", "dead-mans-flats", "exshaw", "banff", "cochrane"],
  },

  // ─────────────────────────────────────────────────────────────── AIRDRIE ──
  {
    slug: "airdrie",
    name: "Airdrie",
    region: "airdrie-corridor",
    city: "Airdrie",
    province: "Alberta",
    coordinates: { lat: 51.2917, lng: -114.0144 },
    tier: 1,
    driveMinutesFromCalgary: 30,
    airspace: {
      classification: "Mixed — control zone edge and local aerodrome",
      nearestAerodrome: "Airdrie aerodrome",
      distanceToAerodromeKm: 4,
      authorizationRequired: true,
      authorizationNote:
        "Airdrie sits between its own aerodrome and the northern edge of the Calgary International control zone. Both constraints apply across parts of the city, so authorisation status is established per address before a shoot is booked.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
      restrictedZones: ["Airdrie aerodrome circuit", "CYYC control zone (northern edge)"],
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Airdrie has been one of the fastest-growing cities in Canada for over a decade, and entire subdivisions change shape between spring and autumn.",
      consequence:
        "Progress that is not photographed at the phase boundary cannot be photographed later — a developer cannot go back and shoot last March.",
      resolution:
        "Repeat progress passes flown from the same coordinates and altitude produce a sequence that reads as one continuous timeline rather than a set of unrelated photos.",
    },
    landmarks: [
      "Coopers Crossing",
      "Kings Heights",
      "Bayside",
      "Windsong",
      "Highway 2",
    ],
    terrain:
      "Flat prairie with shallow drainage. Nothing obstructs a wide frame, which makes subdivision geometry unusually legible from directly overhead.",
    windNote:
      "Exposed prairie with no terrain shelter. Airdrie regularly holds a steadier and stronger surface wind than Calgary 30 kilometres south.",
    seasonalWindow:
      "Long, unobstructed light at both ends of the day. Spring and autumn give the strongest low-angle raking light across new-build streetscapes.",
    bylawNote:
      "City of Airdrie park rules govern launch and landing on municipal land. Development-site work is launched from the site itself with the developer's permission.",
    subjectMix: [
      "new-build-subdivision",
      "construction-progress",
      "commercial-retail",
      "industrial-yard",
    ],
    mobilisationNote:
      "About 30 minutes north of the Calgary base on Highway 2. Straightforward same-day scheduling.",
    faqs: [
      {
        question: "Do you need authorisation to fly a drone in Airdrie?",
        answer:
          "In most of the city, yes. Airdrie sits between its own aerodrome and the northern edge of the Calgary International control zone, so authorisation status depends on the specific address. It is established before the shoot is booked, not on the day.",
      },
      {
        question: "How does aerial progress documentation work for an Airdrie development?",
        answer:
          "Repeat passes are flown from identical coordinates and altitude at agreed intervals. Because the frame does not move, the sequence reads as a single continuous timeline, which is what makes it usable for stakeholder reporting rather than just marketing.",
      },
      {
        question: "Is Airdrie windier than Calgary for drone work?",
        answer:
          "Often, yes. Airdrie sits on open prairie with no terrain shelter, so it frequently holds a steadier and stronger surface wind than Calgary does on the same afternoon. Shoot windows are planned against Airdrie's own conditions.",
      },
    ],
    primaryKeywords: [
      "drone photography Airdrie",
      "Airdrie aerial photography",
      "construction progress drone Airdrie",
    ],
    nearestLocations: ["calgary", "crossfield", "balzac", "bearspaw", "chestermere"],
  },

  // ─────────────────────────────────────────────────────────── CHESTERMERE ──
  {
    slug: "chestermere",
    name: "Chestermere",
    region: "chestermere-area",
    city: "Chestermere",
    province: "Alberta",
    coordinates: { lat: 51.038, lng: -113.8203 },
    tier: 1,
    driveMinutesFromCalgary: 25,
    airspace: {
      classification: "CYYC control zone outer edge",
      nearestAerodrome: "Calgary International Airport (CYYC)",
      distanceToAerodromeKm: 18,
      authorizationRequired: true,
      authorizationNote:
        "The Calgary International control zone boundary runs through the Chestermere area rather than around it. Authorisation status is confirmed per address rather than assumed for the town.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "authorization-required",
    airspaceFact: {
      hook: "Chestermere is organised around a single body of water, and almost every premium address in the town is priced on its relationship to that shoreline.",
      consequence:
        "From the ground a lakefront home shows a fence and a dock. The lake itself — the reason for the price — is a strip of blue behind a hedge.",
      resolution:
        "A shoreline orbit at altitude puts the house and the full water frontage in the same frame, which is the argument the listing is actually making.",
    },
    landmarks: ["Rainbow Falls", "Kinniburgh", "Westmere", "The Cove", "Chestermere Lake"],
    terrain:
      "Flat prairie built around a reservoir. The water is the only significant terrain feature, and it dominates every composition.",
    windNote:
      "Open water with no shelter. Wind crossing the lake arrives unbroken, and surface chop is a reliable visual indicator of conditions aloft.",
    seasonalWindow:
      "Ice-off through freeze-up is the window where the water reads as water. Under ice the lake photographs as a flat grey field and the lakefront argument disappears.",
    bylawNote:
      "City of Chestermere rules govern launch and landing on municipal land and around the lake shoreline reserve.",
    subjectMix: ["lakefront", "luxury-residential", "new-build-subdivision", "tourism-hospitality"],
    mobilisationNote:
      "About 25 minutes east of the Calgary base. One of the shortest mobilisations in the service area.",
    faqs: [
      {
        question: "Can you fly a drone over Chestermere Lake?",
        answer:
          "Yes, with authorisation. The Calgary International control zone boundary runs through the Chestermere area, so authorisation status is confirmed per address. Shoreline work is flown under an Advanced RPAS certificate with that authorisation in hand.",
      },
      {
        question: "When should a Chestermere lakefront listing be shot?",
        answer:
          "Between ice-off and freeze-up. Under ice the lake photographs as a flat grey field, and the waterfront premium the listing is built on simply does not appear in the frame.",
      },
      {
        question: "Why does lakefront property need aerial photography specifically?",
        answer:
          "Because the value is a relationship, not an object. Ground photography can show the house or the water but rarely both, and it cannot show how much frontage the lot actually has. Altitude puts the whole argument in one frame.",
      },
    ],
    primaryKeywords: [
      "drone photography Chestermere",
      "Chestermere lakefront aerial photography",
      "Chestermere real estate drone",
    ],
    nearestLocations: ["calgary", "langdon", "conrich", "strathmore", "airdrie"],
  },

  // ─────────────────────────────────────────────────────────────── OKOTOKS ──
  {
    slug: "okotoks",
    name: "Okotoks",
    region: "foothills-south",
    city: "Okotoks",
    province: "Alberta",
    coordinates: { lat: 50.7256, lng: -113.9749 },
    tier: 2,
    driveMinutesFromCalgary: 35,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Okotoks Air Park",
      distanceToAerodromeKm: 6,
      authorizationRequired: false,
      authorizationNote:
        "Okotoks sits in uncontrolled Class G airspace clear of the Calgary control zone. The Okotoks Air Park creates a localised constraint that is plotted before the flight.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
      restrictedZones: ["Okotoks Air Park circuit"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Okotoks sits in the Sheep River valley with an uninterrupted west-facing view to the Rockies and nothing but ranch land in between.",
      consequence:
        "That mountain sightline is a real part of what a property here sells on, and it only exists in a frame shot late, low and facing west.",
      resolution:
        "Shoots are scheduled into the last hour of light with the approach flown from the east, so the Rockies sit behind the property rather than out of frame.",
    },
    landmarks: ["Sheep River", "Drake Landing", "Cimarron", "Air Ranch", "Highway 2A"],
    terrain:
      "A river valley cutting through foothills ranch country. The Sheep River provides the only significant relief; the rest is long, open rolling ground.",
    windNote:
      "Foothills chinook exposure. West wind arrives off the front ranges with little to slow it.",
    seasonalWindow:
      "The cleanest west-facing golden hour in the service area — there is nothing between Okotoks and the mountains to break the light.",
    bylawNote:
      "Town of Okotoks park rules govern municipal land; river-valley pathway areas carry their own restrictions.",
    subjectMix: ["luxury-residential", "acreage-estate", "new-build-subdivision", "agricultural"],
    mobilisationNote:
      "About 35 minutes south of the Calgary base. Same-day scheduling is routine.",
    faqs: [
      {
        question: "Do you need airspace authorisation to fly a drone in Okotoks?",
        answer:
          "Generally no. Okotoks sits in uncontrolled Class G airspace clear of the Calgary control zone. The Okotoks Air Park creates a localised constraint nearby, so the flight plan is plotted against the circuit before the shoot.",
      },
      {
        question: "Does an Okotoks property really photograph the Rockies?",
        answer:
          "On a clear evening, yes. There is no terrain between Okotoks and the front ranges, so a west-facing frame shot in the last hour of light puts the mountains behind the property. It is one of the few places this close to Calgary where that holds.",
      },
      {
        question: "What is the best season for an Okotoks acreage shoot?",
        answer:
          "Late spring through early autumn, avoiding the peak wildfire-smoke weeks. Smoke is the main threat to the mountain sightline, and without that sightline an Okotoks acreage loses its strongest visual argument.",
      },
    ],
    primaryKeywords: [
      "drone photography Okotoks",
      "Okotoks aerial photography",
      "Okotoks acreage drone video",
    ],
    nearestLocations: ["high-river", "de-winton", "priddis", "aldersyde", "calgary"],
  },

  // ──────────────────────────────────────────────────────────── HIGH RIVER ──
  {
    slug: "high-river",
    name: "High River",
    region: "foothills-south",
    city: "High River",
    province: "Alberta",
    coordinates: { lat: 50.5808, lng: -113.8744 },
    tier: 2,
    driveMinutesFromCalgary: 45,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "High River aerodrome",
      distanceToAerodromeKm: 3,
      authorizationRequired: false,
      authorizationNote:
        "High River sits in uncontrolled Class G airspace. The town aerodrome is close enough that its circuit is plotted on every local flight plan.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
      restrictedZones: ["High River aerodrome circuit"],
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "High River sits on the Highwood River floodplain, and the 2013 flood rebuilt large parts of the town around new berms and drainage works.",
      consequence:
        "Flood mitigation is invisible from the street and highly visible from above, which matters to buyers, insurers and lenders assessing a property here.",
      resolution:
        "A plan-view pass shows the property's actual relationship to the river and the mitigation works — the question a High River buyer is really asking.",
    },
    landmarks: ["Highwood River", "Montrose", "Hampton Hills", "Sunrise Meadows", "Highway 2A"],
    terrain:
      "River floodplain and open ranch land. Very flat, with the Highwood River corridor as the defining feature.",
    windNote:
      "Southern foothills exposure. Strong, persistent west and southwest wind is normal rather than exceptional.",
    seasonalWindow:
      "Long, open light. The river corridor is at its most legible after ice-off and before summer growth closes the banks in.",
    bylawNote:
      "Town of High River rules govern municipal land and the river pathway system.",
    subjectMix: ["agricultural", "new-build-subdivision", "commercial-retail", "municipal-infrastructure"],
    mobilisationNote:
      "About 45 minutes south of the Calgary base. Comfortably same-day, though the wind window drives the timing more than the drive does.",
    faqs: [
      {
        question: "Can you fly a drone in High River?",
        answer:
          "Yes. High River sits in uncontrolled Class G airspace, so no NAV CANADA authorisation is normally required. The town aerodrome is close, so its circuit is plotted on the flight plan before every local shoot.",
      },
      {
        question: "Why does flood context matter for High River aerial photography?",
        answer:
          "Because buyers, insurers and lenders all ask about it. A property's relationship to the Highwood River and to the mitigation works built after 2013 is invisible at street level and immediately clear in a plan view from altitude.",
      },
      {
        question: "Is wind a problem for drone shoots in High River?",
        answer:
          "It is the main scheduling constraint. Southern foothills exposure means strong, persistent west and southwest wind is normal here, so a High River shoot is planned around a wind window rather than a calendar slot.",
      },
    ],
    primaryKeywords: [
      "drone photography High River",
      "High River aerial photography",
      "High River drone video Alberta",
    ],
    nearestLocations: ["okotoks", "aldersyde", "cayley", "nanton", "diamond-valley"],
  },

  // ───────────────────────────────────────────────────────────── STRATHMORE ──
  {
    slug: "strathmore",
    name: "Strathmore",
    region: "wheatland",
    city: "Strathmore",
    province: "Alberta",
    coordinates: { lat: 51.0374, lng: -113.4001 },
    tier: 2,
    driveMinutesFromCalgary: 45,
    airspace: {
      classification: "Class G uncontrolled",
      nearestAerodrome: "Strathmore aerodrome",
      distanceToAerodromeKm: 5,
      authorizationRequired: false,
      authorizationNote:
        "Strathmore sits in uncontrolled Class G airspace well clear of the Calgary control zone. Local aerodrome activity is plotted before the flight.",
      source: "regional-inference",
      lastVerified: "2026-08-31",
    },
    flightStatus: "routine",
    airspaceFact: {
      hook: "Wheatland County is surveyed on the Dominion Land Survey grid, so the land east of Strathmore is a near-perfect one-mile checkerboard.",
      consequence:
        "That geometry is completely invisible from a vehicle and is the single most legible thing about the land from 120 metres — which is how agricultural parcels are actually assessed.",
      resolution:
        "A plan-view pass over a quarter section shows boundaries, access, drainage and cultivation in one frame, delivered as both stills and a moving pass.",
    },
    landmarks: ["Lakewood", "Hillview Estates", "Highway 1", "Wheatland Trail", "Kinsmen Park"],
    terrain:
      "Flat prairie on a survey grid. The flattest ground in the service area, and the most legible from directly overhead.",
    windNote:
      "Fully exposed prairie. Wind here is unbroken from any direction and is the primary scheduling variable.",
    seasonalWindow:
      "Cultivation cycle drives the look. Post-seeding green, mid-summer canola yellow and post-harvest stubble each read completely differently from the air.",
    bylawNote:
      "Town of Strathmore rules govern municipal land; county parcels are launched from with the landowner's permission.",
    subjectMix: ["agricultural", "industrial-yard", "commercial-retail", "new-build-subdivision"],
    mobilisationNote:
      "About 45 minutes east of the Calgary base on Highway 1. Same-day capable.",
    faqs: [
      {
        question: "Can you fly a drone over farmland near Strathmore?",
        answer:
          "Yes. Wheatland County is uncontrolled Class G airspace, so no authorisation is normally required. Launch is arranged from the parcel itself with the landowner's permission, and local aerodrome activity is plotted beforehand.",
      },
      {
        question: "What is the best time in the growing season to shoot a quarter section?",
        answer:
          "It depends on the argument. Post-seeding shows drainage and soil variation, mid-summer shows crop health and canola colour, and post-harvest stubble shows boundaries and access most clearly. The date is chosen from what the parcel needs to prove.",
      },
      {
        question: "How does aerial imagery help sell agricultural land?",
        answer:
          "It shows what a buyer cannot otherwise verify: exact boundaries, road and water access, drainage patterns and how much of the parcel is genuinely workable. On the survey grid east of Strathmore, all four are obvious from directly overhead.",
      },
    ],
    primaryKeywords: [
      "drone photography Strathmore",
      "agricultural drone photography Strathmore",
      "Strathmore aerial photography Alberta",
    ],
    nearestLocations: ["chestermere", "standard", "rosebud", "langdon", "hussar"],
  },
];
